
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {buildTriangleBVHForMeshes, nearestBetweenBVHs, nearestPointOnBVH} from "./TriangleBVH.js";
import {forEachLoadedNodeInBox} from "../PointCloudQuery/PointCloudQuery.js";

// Feature D (PointView spec, Sprint 3) - the two "første version"
// measurement algorithms, kept as plain functions independent of any
// viewer/UI state so they're easy to reason about and (per the sprint's
// own Definition of Done) test with known geometry. index.html owns
// picking which elements/point cloud to pass in, progress UI, and
// ViewerState persistence.

// IFC ↔ IFC: exact nearest triangle pair via bounding-box-pruned BVH
// traversal (see TriangleBVH.js). Overlapping/colliding elements come back
// essentially at distance 0 (see triangleTriangleDistance's own comment on
// why a full intersection predicate isn't needed on top of this).
export function computeIfcIfcShortestDistance(meshesA, meshesB){
	let bvhA = buildTriangleBVHForMeshes(meshesA);
	let bvhB = buildTriangleBVHForMeshes(meshesB);

	let result = nearestBetweenBVHs(bvhA, bvhB);
	if(!result){
		return {status: "insufficient_data"};
	}

	return {
		status: "done",
		distance: result.distance,
		pointA: result.pointOnA,
		pointB: result.pointOnB,
	};
}

// IFC ↔ punktsky: "korteste OBSERVEREDE afstand" - nearest currently-loaded
// point to the element's BVH, within searchBox. Processes points in
// batches with a yield in between (see PointCloudQuery.js's own comment on
// why this runs on the main thread with yields rather than a real Web
// Worker - the same, already-established reasoning as IFCModule.js's
// buildPropertyIndex) so the UI stays responsive and the search can be
// cancelled between batches. Never returns distance 0 for "nothing found" -
// status "no_points" carries no distance at all.
export async function computeIfcPointCloudShortestDistance(elementBvh, pointcloud, searchBox, options = {}){
	let {onProgress, isCancelled, onYield = () => new Promise(r => setTimeout(r, 0)), batchSize = 20000} = options;

	let best = {distance: Infinity, ifcPoint: null, cloudPoint: null};
	let examinedPoints = 0;
	let examinedNodes = 0;
	let batch = [];

	let processBatch = async () => {
		for(let p of batch){
			let hit = nearestPointOnBVH(elementBvh, p, best.distance);
			if(hit){
				best.distance = hit.distance;
				best.ifcPoint = hit.point;
				best.cloudPoint = p.clone();
			}
		}
		examinedPoints += batch.length;
		batch = [];
		if(onProgress){
			onProgress({examinedPoints, examinedNodes, currentBest: best.distance === Infinity ? null : best.distance});
		}
		await onYield();
	};

	let nodeList = [];
	forEachLoadedNodeInBox(pointcloud, searchBox, (node) => nodeList.push(node));

	let cancelled = false;
	for(let node of nodeList){
		if(isCancelled && isCancelled()){
			cancelled = true;
			break;
		}
		examinedNodes++;
		let posAttr = node.geometry.attributes.position;
		let v = new THREE.Vector3();
		for(let i = 0; i < posAttr.count; i++){
			v.fromBufferAttribute(posAttr, i).applyMatrix4(pointcloud.matrixWorld);
			if(!searchBox.containsPoint(v)){
				continue;
			}
			batch.push(v.clone());
			if(batch.length >= batchSize){
				await processBatch();
				if(isCancelled && isCancelled()){
					cancelled = true;
					break;
				}
			}
		}
		if(cancelled){
			break;
		}
	}
	if(!cancelled && batch.length > 0){
		await processBatch();
	}

	if(cancelled){
		return {
			status: "cancelled",
			distance: best.distance === Infinity ? null : best.distance,
			ifcPoint: best.ifcPoint, cloudPoint: best.cloudPoint,
			examinedPoints, examinedNodes,
		};
	}
	if(best.distance === Infinity){
		return {status: "no_points", examinedPoints, examinedNodes};
	}
	return {
		status: "done",
		distance: best.distance,
		ifcPoint: best.ifcPoint, cloudPoint: best.cloudPoint,
		examinedPoints, examinedNodes,
	};
}

// A simple local-density estimate around a result point - "estimeret
// punkttæthed omkring resultatet" (spec, kvalitetspanel) - points per m³
// within `radius` of `center`, counted from whatever nodes are already
// loaded (same simplification as the search itself).
export function estimatePointDensity(pointcloud, center, radius){
	let box = new THREE.Box3(
		new THREE.Vector3(center.x - radius, center.y - radius, center.z - radius),
		new THREE.Vector3(center.x + radius, center.y + radius, center.z + radius),
	);
	let count = 0;
	let v = new THREE.Vector3();
	forEachLoadedNodeInBox(pointcloud, box, (node) => {
		let posAttr = node.geometry.attributes.position;
		for(let i = 0; i < posAttr.count; i++){
			v.fromBufferAttribute(posAttr, i).applyMatrix4(pointcloud.matrixWorld);
			if(v.distanceTo(center) <= radius){
				count++;
			}
		}
	});
	let volume = (4 / 3) * Math.PI * radius * radius * radius;
	return count / volume;
}
